import { memo, useMemo } from "react";

import {
  type Maybe,
  type SelectedOption,
} from "@shopify/hydrogen-react/storefront-api-types";

import { VariantSelectorView } from "@components/VariantSelector/VariantSelector.view";
import {
  type CartProductVariant,
  type CartRenderVariantOption,
} from "@tablescape/state";

type Props = {
  context?: "productForm" | "productSelector" | "cart";
  displaySingleVisibleVariant?: boolean;
  optionCategories?: Maybe<string[]>;
  productName?: string;
  renderOption: CartRenderVariantOption;
  selected: CartProductVariant | undefined;
  selectedOption: SelectedOption;
  setSelected: (variant: CartProductVariant["id"]) => void;
  showProductName?: boolean;
  showThumbnail?: boolean;
  size: "tiny" | "mini" | "regular" | "small" | "large";
  type: "artwork" | "color" | "style" | "generic";
  variants: CartProductVariant[];
};

const VariantSelectorBase = ({
  context,
  displaySingleVisibleVariant = false,
  optionCategories,
  productName,
  renderOption,
  selected,
  selectedOption,
  setSelected,
  showProductName,
  showThumbnail,
  size,
  type,
  variants,
}: Props) => {
  const visibleVariantCount = useMemo(
    () => variants.filter((variant) => !variant.hidden).length,
    [variants]
  );

  const shouldDisplayVariantSelector = useMemo(
    () => visibleVariantCount > (displaySingleVisibleVariant ? 0 : 1),
    [displaySingleVisibleVariant, visibleVariantCount]
  );

  return (
    shouldDisplayVariantSelector && (
      <VariantSelectorView
        context={context}
        optionCategories={optionCategories}
        productName={productName}
        renderOption={renderOption}
        selected={selected}
        selectedOption={selectedOption}
        setSelected={setSelected}
        showProductName={showProductName}
        showThumbnail={showThumbnail}
        size={size}
        type={type}
        variants={variants}
      />
    )
  );
};

export const VariantSelector = memo(VariantSelectorBase);
